import { useState, useEffect } from "react";
import { useResourcePersonEarnings } from "../store/resourcePerson/resourcePersonStore";

function formatDate(dateStr) {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-NG", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function formatNaira(amount) {
  return "₦" + Number(amount).toLocaleString("en-NG");
}

function metric(data, keys) {
  if (typeof data === "number") return data;
  for (const key of keys) {
    if (data && data[key] !== undefined) return Number(data[key]) || 0;
  }
  return 0;
}

function earningsRow(item, index) {
  const student = item.student || item.user || {};
  const name = item.studentName || item.userName || student.name || item.name || "Student";
  return {
    id: item.id || item.transactionId || index,
    name,
    email: item.studentEmail || item.userEmail || student.email || "",
    course: item.courseTitle || item.resourceTitle || item.title || item.description || item.type || "Earnings transaction",
    amount: metric(item, ["amount", "credit", "grossAmount", "total"]),
    date: item.createdAt || item.created_at || item.date || item.occurredAt || item.occurred_at,
    status: item.status === "pending" ? "Pending" : item.status === "completed" ? "Completed" : item.status || "—",
  };
}

function recordsOf(value) {
  if (Array.isArray(value)) return value;
  return value?.items || value?.entries || value?.transactions || [];
}

function periodLabel(item, key) {
  const value = item?.[key] || item?.date || item?.week || item?.month;
  if (!value) return "—";
  if (key === "month" && /^\d{4}-\d{2}$/.test(String(value))) {
    const [year, month] = String(value).split("-").map(Number);
    return new Intl.DateTimeFormat("en-NG", {month: "long", year: "numeric"}).format(new Date(year, month - 1, 1));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat("en-NG", {month: "short", day: "numeric", year: "numeric"}).format(date);
}

function Pagination({page, pages, total, loading, onChange}) {
  if (pages <= 1 && Number(total) === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-100 px-4 py-3">
      <span className="text-xs text-stone-500">Page {page} of {Math.max(pages, 1)} · {total} total</span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={loading || page <= 1}
          className="rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={loading || page >= pages}
          className="rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}

// ── Withdrawal Modal ──────────────────────────────────────────────────────────
function WithdrawalModal({ available, onClose }) {
  const [type, setType] = useState("");
  const [amount, setAmount] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const enteredNum = parseFloat(amount) || 0;
  const overLimit  = enteredNum > available;
  
  // Only require account details for Bank Transfer
  const requiresAccountDetails = type === "Bank Transfer";
  const hasAccountDetails = requiresAccountDetails 
    ? accountName && accountNumber && bankName 
    : true;
  
  const invalid = !type || !amount || enteredNum <= 0 || overLimit || !hasAccountDetails;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full shadow-2xl border border-stone-100 flex flex-col max-h-[90vh]"
        style={{ maxWidth: 440 }}
        onClick={e => e.stopPropagation()}
      >
        {submitted ? (
          <div className="flex flex-col items-center py-12 px-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="font-sans font-bold text-xl text-stone-900 mb-2">Request Submitted</h3>
            <p className="text-sidebar text-sm mb-1">
              Withdrawal of <span className="font-bold text-stone-800">{formatNaira(enteredNum)}</span> via{" "}
              <span className="font-semibold text-stone-700">{type}</span> has been queued.
            </p>
            <p className="text-stone-400 text-xs mb-8">Processing takes 1–3 business days.</p>
            <button
              onClick={onClose}
              className="px-8 py-2.5 rounded-xl bg-stone-900 text-white text-sm font-bold hover:bg-stone-700 transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {/* Header - Fixed */}
            <div className="px-6 pt-6 pb-4 border-b border-stone-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="font-sans font-bold text-xl text-stone-900">Request Withdrawal</h3>
                <p className="text-stone-400 text-xs mt-0.5">50% platform commission already deducted</p>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-stone-100 hover:bg-gray-100 shadow-md flex items-center justify-center transition-colors"
              >
                <svg className="w-4 h-4 text-sidebar" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Available balance - Fixed */}
            <div className="mx-6  rounded-xl bg-stone-100 px-4 py-3 flex items-center justify-between flex-shrink-0">
              <p className="text-xs uppercase tracking-widest font-bold text-sidebar">Available to withdraw</p>
              <p className="font-sans font-bold text-xl text-stone-900">{formatNaira(available)}</p>
            </div>

            {/* Form - Scrollable */}
            <div className="px-6 py-5 flex flex-col gap-5 overflow-y-auto">
              {/* Withdrawal type */}
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-sidebar block mb-2">
                  Withdrawal Method
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {["Bank Transfer", "PayStack", "Flutterwave"].map(t => (
                    <button
                      key={t}
                      onClick={() => setType(t)}
                      className={`py-2.5 px-2 rounded-xl text-xs font-bold border transition-all ${
                        type === t
                          ? "bg-stone-900 text-white border-stone-900"
                          : "bg-white border-stone-200 text-stone-600 hover:border-stone-400"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Amount input */}
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-sidebar block mb-2">
                  Amount to Withdraw
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 font-bold text-sm select-none">₦</span>
                  <input
                    type="number"
                    min="0"
                    max={available}
                    placeholder="0"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    className={`w-full pl-8 pr-4 py-3 rounded-xl border text-sm font-semibold text-stone-800 placeholder-stone-300 focus:outline-none focus:ring-2 transition ${
                      overLimit
                        ? "border-red-300 focus:ring-red-100"
                        : "border-stone-200 focus:ring-orange-100 focus:border-orange-400"
                    }`}
                  />
                </div>
                {overLimit && (
                  <p className="text-xs text-red-600 mt-1.5">Exceeds available balance of {formatNaira(available)}.</p>
                )}
                <button
                  onClick={() => setAmount(String(available))}
                  className="text-xs text-orange-700 font-semibold mt-1.5 hover:underline"
                >
                  Withdraw max ({formatNaira(available)})
                </button>
              </div>

              {/* Account Details - Only show for Bank Transfer */}
              {requiresAccountDetails && (
                <div className="space-y-4 p-4 bg-stone-50 rounded-xl border border-stone-200">
                  <p className="text-xs font-bold text-stone-700 uppercase tracking-wider">Bank Account Details</p>
                  
                  {/* Account Name */}
                  <div>
                    <label className="text-xs font-semibold text-stone-600 block mb-1.5">
                      Account Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter account name"
                      value={accountName}
                      onChange={e => setAccountName(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl border border-stone-200 bg-white text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-orange-100 focus:border-orange-400 transition"
                    />
                  </div>

                  {/* Account Number */}
                  <div>
                    <label className="text-xs font-semibold text-stone-600 block mb-1.5">
                      Account Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      placeholder="Enter account number"
                      value={accountNumber}
                      onChange={e => setAccountNumber(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl border border-stone-200 bg-white text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-orange-100 focus:border-orange-400 transition"
                    />
                  </div>

                  {/* Bank Name */}
                  <div>
                    <label className="text-xs font-semibold text-stone-600 block mb-1.5">
                      Bank Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter bank name"
                      value={bankName}
                      onChange={e => setBankName(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl border border-stone-200 bg-white text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-orange-100 focus:border-orange-400 transition"
                    />
                  </div>
                </div>
              )}

              {/* Submit */}
              <button
                onClick={() => !invalid && setSubmitted(true)}
                disabled={invalid}
                className={`w-full py-3 rounded-xl text-sm font-bold uppercase tracking-wide transition-all flex-shrink-0 ${
                  invalid
                    ? "bg-gray-100 shadow-md text-stone-400 cursor-not-allowed"
                    : "bg-stone-900 text-white hover:bg-orange-800 active:scale-95"
                }`}
              >
                Confirm Withdrawal
              </button>

              {/* Helper text */}
              {requiresAccountDetails && (
                <p className="text-xs text-stone-500 text-center flex-shrink-0">
                  Please ensure your account details are correct before submitting
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SubscriptionsPage() {
  const [ledgerPage, setLedgerPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(1);
  const [reservedPage, setReservedPage] = useState(1);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState("");
  const [historyError, setHistoryError] = useState("");
  const {
    earningsSummary,
    earningsAnalytics,
    earningsPending,
    earningsAvailable,
    earningsReserved,
    earningsHistory,
    earningsHistoryTotal,
    earningsHistoryPages,
    earningsLedger,
    earningsLedgerTotal,
    earningsLedgerPages,
    earningsLoading,
    earningsError,
    fetchEarningsLedger,
    fetchEarningsHistory,
    fetchAllEarnings,
  } = useResourcePersonEarnings();

  useEffect(() => {
    fetchAllEarnings();
  }, [fetchAllEarnings]);

  const summary = earningsSummary || {};
  const analytics = earningsAnalytics || {};
  const availableToWithdraw = metric(earningsAvailable, ["available", "availableBalance", "total", "amount", "balance"]) ||
    metric(summary, ["available", "availableBalance", "availableToWithdraw"]);
  const pendingAmount = metric(summary, ["pending", "pendingBalance"]) || metric(earningsPending, ["total", "pending"]);
  const reservedAmount = metric(summary, ["reserved", "reservedBalance"]) || metric(earningsReserved, ["total", "reserved"]);
  const withdrawn = metric(summary, ["totalWithdrawn", "withdrawn"]);
  const thisMonth = summary.thisMonth || {};
  const pendingRows = recordsOf(earningsPending);
  const reservedRows = recordsOf(earningsReserved);
  const pendingTotal = earningsPending?.total ?? pendingRows.length;
  const reservedTotal = earningsReserved?.total ?? reservedRows.length;
  const pendingPages = Math.max(1, Math.ceil(pendingRows.length / 10));
  const reservedPages = Math.max(1, Math.ceil(reservedRows.length / 10));
  const visiblePendingRows = pendingRows.slice((pendingPage - 1) * 10, pendingPage * 10);
  const visibleReservedRows = reservedRows.slice((reservedPage - 1) * 10, reservedPage * 10);
  const weekly = analytics.weekly || [];
  const monthly = analytics.monthly || [];
  const weeklyMax = Math.max(0, ...weekly.map((item) => Number(item.earnings || 0)));
  const monthlyMax = Math.max(0, ...monthly.map((item) => Number(item.earnings || 0)));

  const changeLedgerPage = async (nextPage) => {
    if (nextPage < 1 || nextPage > earningsLedgerPages) return;
    setLedgerLoading(true);
    setLedgerError("");
    try {
      const result = await fetchEarningsLedger({page: nextPage, limit: 10});
      if (result.success) setLedgerPage(nextPage);
      else setLedgerError(result.error || "Could not load the requested ledger page.");
    } finally {
      setLedgerLoading(false);
    }
  };
  const changeHistoryPage = async (nextPage) => {
    if (nextPage < 1 || nextPage > earningsHistoryPages) return;
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const result = await fetchEarningsHistory({page: nextPage, limit: 10});
      if (result.success) setHistoryPage(nextPage);
      else setHistoryError(result.error || "Could not load the requested history page.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const renderRows = (items, emptyMessage) => items.length ? (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
          <tr>
            <th className="px-4 py-3">Description</th>
            <th className="px-4 py-3">Reference</th>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const row = earningsRow(item, index);
            return (
              <tr key={row.id} className="border-t border-stone-100">
                <td className="px-4 py-3">
                  <p className="m-0 font-semibold text-stone-800">{row.course}</p>
                  <p className="m-0 mt-0.5 text-xs text-stone-500">{row.name}{row.email ? ` · ${row.email}` : ""}</p>
                </td>
                <td className="px-4 py-3 text-stone-500">{item.reference || item.transactionReference || item.transactionId || item.id || "—"}</td>
                <td className="px-4 py-3 text-stone-500">{row.date ? formatDate(row.date) : "—"}</td>
                <td className="px-4 py-3"><span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-semibold text-stone-700">{row.status}</span></td>
                <td className="px-4 py-3 text-right font-bold text-stone-800">{formatNaira(row.amount)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  ) : <p className="m-0 px-5 py-8 text-center text-sm text-stone-500">{emptyMessage}</p>;

  return (
    <div className="min-h-screen bg-stone-50 font-sans text-stone-900 antialiased">
      <main className="mx-auto max-w-6xl space-y-6 px-5 py-8">
        <header>
          <p className="m-0 text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Resource person finances</p>
          <h1 className="mb-0 mt-1 text-3xl font-bold leading-tight text-stone-900">Earnings</h1>
          <p className="mb-0 mt-1 text-sm text-stone-500">A clear view of your balances, transactions and earnings trends.</p>
        </header>

        {earningsError && <p role="alert" className="m-0 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{earningsError}</p>}

        <section className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" aria-label="Earnings summary">
          {[
            ["Lifetime earnings", metric(summary, ["lifetime", "lifetimeEarnings", "totalEarnings"])],
            ["Available to withdraw", availableToWithdraw],
            ["Pending", pendingAmount],
            ["Reserved", reservedAmount],
            ["Total withdrawn", withdrawn],
            ["This month", metric(thisMonth, ["earnings", "amount"])],
          ].map(([label, value]) => (
            <article key={label} className="rounded-xl border border-stone-100 bg-white p-3 shadow-sm">
              <p className="m-0 text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</p>
              <p className="mb-0 mt-1 text-base font-bold text-stone-900">{formatNaira(value)}</p>
              {label === "This month" ? <p className="mb-0 mt-1 text-xs text-stone-500">{metric(thisMonth, ["count"])} transactions</p> : null}
              {label === "Available to withdraw" && earningsAvailable?.availableSince ? (
                <p className="mb-0 mt-1 text-xs text-stone-500">Available since {formatDate(earningsAvailable.availableSince)}</p>
              ) : null}
            </article>
          ))}
        </section>

        {earningsLoading ? <p className="m-0 text-center text-sm text-stone-500">Loading earnings…</p> : null}

        <section className="grid gap-4 lg:grid-cols-2" aria-label="Earnings analytics">
          <article className="rounded-2xl border border-stone-100 bg-white p-5 shadow-sm lg:col-span-2">
            <p className="m-0 text-xs font-semibold uppercase tracking-wide text-stone-500">Analytics total</p>
            <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-2xl font-bold text-stone-900">{formatNaira(metric(analytics.total, ["earnings", "amount", "total"]))}</span>
              <span className="text-sm text-stone-500">{metric(analytics.total, ["count"])} transactions</span>
            </div>
          </article>
          {[
            ["Weekly earnings", weekly, "week", weeklyMax],
            ["Monthly earnings", monthly, "month", monthlyMax],
          ].map(([title, points, labelKey, max]) => (
            <article key={title} className="rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
              <h2 className="m-0 text-base font-bold text-stone-900">{title}</h2>
              {points.length ? (
                <ul className="mb-0 mt-4 grid list-none gap-3 p-0">
                  {points.map((point, index) => {
                    const amount = Number(point.earnings || 0);
                    const width = max ? `${Math.max(2, amount / max * 100)}%` : "0%";
                    return (
                      <li key={`${point[labelKey]}-${index}`}>
                        <div className="mb-1 flex justify-between gap-3 text-xs">
                          <span className="text-stone-500">{periodLabel(point, labelKey)}</span>
                          <span className="font-semibold text-stone-800">{formatNaira(amount)}</span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                          <div className="h-full rounded-full bg-blue-600" style={{width}} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : <p className="mb-0 mt-4 text-sm text-stone-500">No {title.toLowerCase()} data available.</p>}
            </article>
          ))}
        </section>

        <section className="grid gap-4 lg:grid-cols-2">
          {[
            ["Pending earnings", pendingRows, earningsPending?.total, "No pending earnings."],
            ["Reserved earnings", reservedRows, earningsReserved?.total, "No reserved earnings."],
          ].map(([title, items, total, emptyMessage]) => (
            <article key={title} className="overflow-hidden rounded-2xl border border-stone-100 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-stone-100 px-5 py-4">
                <h2 className="m-0 text-base font-bold text-stone-900">{title}</h2>
                <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-semibold text-stone-600">{total ?? items.length}</span>
              </div>
              {items.length ? (
                <ul className="m-0 list-none divide-y divide-stone-100 p-0">
                  {(title === "Pending earnings" ? visiblePendingRows : visibleReservedRows).map((item, index) => {
                    const row = earningsRow(item, index);
                    return (
                      <li key={row.id} className="flex items-center justify-between gap-3 px-5 py-3">
                        <div className="min-w-0">
                          <p className="m-0 truncate text-sm font-semibold text-stone-800">{row.course}</p>
                          <p className="m-0 mt-0.5 truncate text-xs text-stone-500">{row.name}{row.date ? ` · ${formatDate(row.date)}` : ""}</p>
                        </div>
                        <span className="shrink-0 text-sm font-bold text-stone-800">{formatNaira(row.amount)}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : <p className="m-0 px-5 py-6 text-sm text-stone-500">{emptyMessage}</p>}
              <Pagination
                page={title === "Pending earnings" ? pendingPage : reservedPage}
                pages={title === "Pending earnings" ? pendingPages : reservedPages}
                total={total}
                loading={false}
                onChange={title === "Pending earnings" ? setPendingPage : setReservedPage}
              />
            </article>
          ))}
        </section>

        {[
          ["Earnings ledger", earningsLedger, earningsLedgerTotal, ledgerPage, earningsLedgerPages, ledgerLoading, changeLedgerPage, "No ledger entries."],
          ["Earnings history", earningsHistory, earningsHistoryTotal, historyPage, earningsHistoryPages, historyLoading, changeHistoryPage, "No earnings history."],
        ].map(([title, items, total, page, pages, loading, onChange, emptyMessage]) => (
          <section key={title} className="overflow-hidden rounded-2xl border border-stone-100 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-stone-100 px-5 py-4">
              <h2 className="m-0 text-base font-bold text-stone-900">{title}</h2>
              <span className="text-xs text-stone-500">{total} records</span>
            </div>
            {title === "Earnings ledger" && ledgerError ? <p role="alert" className="m-0 px-5 pt-3 text-sm text-red-700">{ledgerError}</p> : null}
            {title === "Earnings history" && historyError ? <p role="alert" className="m-0 px-5 pt-3 text-sm text-red-700">{historyError}</p> : null}
            {loading ? <p className="m-0 px-5 py-8 text-center text-sm text-stone-500">Loading transactions…</p> : renderRows(items, emptyMessage)}
            <Pagination page={page} pages={pages} total={total} loading={loading} onChange={onChange} />
          </section>
        ))}

        <p className="m-0 text-center text-xs text-stone-500">
          Payouts are processed on the 1st and 15th of each month ·{" "}
          <a href="https://filloptech.com/resource-person/support" className="font-semibold text-blue-700 hover:underline">Contact support</a>
        </p>
      </main>
    </div>
  );
}